// The current-sensor screen — DeviceID 82 (single-phase) and 16 (three-phase).
//
// Base layout is Flutter's singlePhaseScreen.dart / threePhaseScreen.dart:
//   POWER graph · [x] Show Current Graph · CURRENT graph · Pick a Date
// with one line per `phase` on the three-phase meter (1 red, 2 green, 3 blue)
// and a single FontColor line on the single-phase one.
//
// On top of that, and NOT in the Flutter app, this carries the desktop card's
// From/To range and Total Consumption, because a phone user wants the kWh and
// the ₹ estimate too. The desktop packs those controls onto one row, which is
// what made this screen unusable at phone width — here they are stacked
// full-width instead.
//
// The range is the source of truth: the graphs and the consumption always
// describe exactly what is in From/To. "Pick a Date" is a shortcut that sets the
// range to one whole day, which is what the Flutter screen does implicitly.
//
// All the loading and the kWh maths live in core/devices/phase.js, shared with
// the desktop card so the two can never disagree about the same range.

import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useContentHeight } from '../../components/MobileShell';
import {
  Box, Typography, Button, Checkbox, CircularProgress, TextField, Divider, useTheme,
} from '@mui/material';
import CalendarTodayIcon from '@mui/icons-material/CalendarToday';
import {
  ResponsiveContainer, LineChart, Line, XAxis, YAxis, CartesianGrid,
} from 'recharts';
import DeviceAppBar from './components/DeviceAppBar';
import {
  PHASE_COLORS, PHASE_LABELS, axisTick, consumptionOf, downsample, endOfToday,
  loadRange, phasesPresent, startOfToday, toChartSeries, toLocalInput, xTickStep,
  yTicks,
} from '../../../../core/devices/phase';

export default function PhaseScreen({ device, threePhase = false }) {
  const theme = useTheme();
  const contentH = useContentHeight();
  const c = theme.sli;
  const uno = device?.UniqueNumber;

  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showCurrent, setShowCurrent] = useState(false);

  const [from, setFrom] = useState(() => toLocalInput(startOfToday()));
  const [to, setTo] = useState(() => toLocalInput(endOfToday()));
  const [rangeError, setRangeError] = useState('');

  const load = useCallback(async (fromStr, toStr) => {
    const fromD = new Date(fromStr);
    const toD = new Date(toStr);
    if (Number.isNaN(+fromD) || Number.isNaN(+toD) || toD <= fromD) {
      setRangeError('Pick a valid From/To range');
      setRows([]);
      setLoading(false);
      return;
    }
    setRangeError('');
    setLoading(true);
    try {
      setRows(await loadRange(uno, fromD, toD));
    } finally {
      setLoading(false);
    }
  }, [uno]);

  // Load today on open. Editing the fields deliberately does NOT reload — you
  // apply a range with Get, so the graphs and the kWh always describe the range
  // that was actually fetched, never a half-typed one. That is why `from`/`to`
  // are not dependencies here.
  useEffect(() => {
    load(from, to);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [uno]);

  // ── Responsive sizing (the Dart's clamps) ────────────────────────────────
  const geom = useMemo(() => {
    // ⚠️ THE CONTENT AREA, NOT THE WINDOW. The window also holds the identity
    // strip and the company footer, which this screen does not get — sizing
    // from it overflows by exactly their height, and the overflow is clipped
    // rather than scrolled. See ui/mobile/components/MobileShell.
    const availH = contentH - 64;
    const panelWidth = Math.min(window.innerWidth, 640);
    return {
      panelWidth,
      graphWidth: Math.min(600, Math.max(240, panelWidth - 40)),
      graphHeight: Math.min(320, Math.max(160, availH * 0.3)),
    };
  }, [contentH]);

  const present = phasesPresent(rows);
  const lines = threePhase ? present : [1];
  const consumption = rows.length ? consumptionOf(rows) : null;

  // One whole day — the Flutter "Pick a Date" behaviour.
  const onPickDate = (e) => {
    const v = e.target.value;
    if (!v) return;
    const [y, m, d] = v.split('-').map(Number);
    const f = toLocalInput(new Date(y, m - 1, d, 0, 0));
    const t = toLocalInput(new Date(y, m - 1, d, 23, 59));
    setFrom(f);
    setTo(t);
    load(f, t);
  };

  const fieldSx = {
    '& .MuiInputLabel-root': { color: `${c.text}B3` },
    '& .MuiOutlinedInput-root': {
      color: c.text,
      borderRadius: '12px',
      '& fieldset': { borderColor: `${c.text}66` },
      '&:hover fieldset': { borderColor: c.text },
      '&.Mui-focused fieldset': { borderColor: c.text },
    },
    // The native picker indicator is black-on-dark by default.
    '& input::-webkit-calendar-picker-indicator': { filter: 'invert(1)' },
  };

  const renderGraph = (dataKey) => {
    const data = downsample(toChartSeries(rows, dataKey));
    const step = xTickStep(data.length, threePhase);
    const ticks = yTicks(rows, dataKey);

    return (
      <Box sx={{ width: '100%', maxWidth: geom.graphWidth, height: geom.graphHeight, py: 1 }}>
        <ResponsiveContainer width="100%" height="100%">
          <LineChart data={data} margin={{ top: 8, right: 8, bottom: 4, left: 0 }}>
            <CartesianGrid stroke={`${c.text}33`} />
            <XAxis
              dataKey="t"
              interval={Math.max(0, step - 1)}
              tick={{ fill: c.text, fontSize: 9 }}
              stroke={`${c.text}66`}
            />
            <YAxis
              width={45}
              domain={[0, ticks[ticks.length - 1]]}
              ticks={ticks}
              tick={{ fill: c.text, fontSize: 10 }}
              stroke={`${c.text}66`}
              tickFormatter={(v) => axisTick(v, dataKey)}
            />
            {lines.map((p) => (
              <Line
                key={p}
                type="linear"
                dataKey={`p${p}`}
                name={PHASE_LABELS[p]}
                stroke={threePhase ? (PHASE_COLORS[p] || '#9E9E9E') : c.text}
                strokeWidth={1.5}
                dot={false}
                isAnimationActive={false}
                connectNulls
              />
            ))}
          </LineChart>
        </ResponsiveContainer>
      </Box>
    );
  };

  const graphCaption = (label) => (
    <Typography sx={{ color: `${c.text}B3`, fontSize: 12, letterSpacing: '1px' }}>
      {label}
    </Typography>
  );

  return (
    <Box sx={{ height: '100%', bgcolor: c.back, display: 'flex', flexDirection: 'column' }}>
      <DeviceAppBar device={device} subtitle={device?.LocationName ?? ''} />

      <Box sx={{ flex: 1, minHeight: 0, overflowY: 'auto', display: 'flex', justifyContent: 'center' }}>
        <Box sx={{
          maxWidth: geom.panelWidth,
          width: '100%',
          p: 2.5,
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
        }}
        >
          {/* Legend — only useful when more than one line is drawn. */}
          {threePhase && present.length > 1 && (
            <Box sx={{ display: 'flex', gap: 1.5, flexWrap: 'wrap', justifyContent: 'center', pb: 1 }}>
              {present.map((p) => (
                <Box key={p} sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                  <Box sx={{ width: 14, height: 3, bgcolor: PHASE_COLORS[p], borderRadius: 1 }} />
                  <Typography sx={{ color: c.text, fontSize: 12 }}>{PHASE_LABELS[p]}</Typography>
                </Box>
              ))}
            </Box>
          )}

          {loading ? (
            <Box sx={{
              width: '100%', height: geom.graphHeight,
              display: 'flex', alignItems: 'center', justifyContent: 'center',
            }}
            >
              <CircularProgress sx={{ color: c.text }} />
            </Box>
          ) : rangeError ? (
            <Typography sx={{ color: '#F44336', py: 4 }}>{rangeError}</Typography>
          ) : rows.length === 0 ? (
            <Box sx={{
              width: '100%', height: geom.graphHeight,
              display: 'flex', alignItems: 'center', justifyContent: 'center',
            }}
            >
              <Typography sx={{ color: `${c.text}B3` }}>No readings in this range</Typography>
            </Box>
          ) : (
            <>
              {graphCaption('POWER (W)')}
              {renderGraph('power')}
              {showCurrent && (
                <>
                  {graphCaption('CURRENT (A)')}
                  {renderGraph('current')}
                </>
              )}
            </>
          )}

          <Box sx={{ display: 'flex', alignItems: 'center', py: 1.25 }}>
            <Checkbox
              checked={showCurrent}
              onChange={(e) => setShowCurrent(e.target.checked)}
              sx={{ color: '#BDBDBD', '&.Mui-checked': { color: '#000' } }}
            />
            <Typography sx={{ color: c.text, fontSize: 16 }}>Show Current Graph</Typography>
          </Box>

          <Divider sx={{ width: '100%', borderColor: `${c.text}33`, my: 1 }} />

          {/* From / To — stacked, full width. The desktop packs these onto one
              row with the Get button, which does not fit a phone. */}
          <Box sx={{ width: '100%', display: 'flex', flexDirection: 'column', gap: 1.5 }}>
            <TextField
              type="datetime-local"
              label="From Date"
              value={from}
              onChange={(e) => setFrom(e.target.value)}
              InputLabelProps={{ shrink: true }}
              fullWidth
              sx={fieldSx}
            />
            <TextField
              type="datetime-local"
              label="To Date"
              value={to}
              onChange={(e) => setTo(e.target.value)}
              InputLabelProps={{ shrink: true }}
              fullWidth
              sx={fieldSx}
            />
            <Button
              fullWidth
              onClick={() => load(from, to)}
              disabled={loading}
              sx={{
                bgcolor: c.button,
                color: c.text,
                py: 1.5,
                borderRadius: '12px',
                textTransform: 'none',
                fontSize: 16,
                '&:hover': { bgcolor: c.button },
              }}
            >
              {loading ? <CircularProgress size={20} sx={{ color: c.text }} /> : 'Get'}
            </Button>
          </Box>

          {/* Total Consumption for exactly the range above. */}
          <Box sx={{ width: '100%', pt: 2 }}>
            <Typography sx={{
              color: `${c.text}B3`, fontSize: 12, letterSpacing: '1px', pb: 0.5,
            }}
            >
              TOTAL CONSUMPTION
            </Typography>
            {consumption ? (
              <Box sx={{ display: 'flex', gap: 1.5, alignItems: 'baseline', flexWrap: 'wrap' }}>
                <Typography sx={{ color: c.text, fontSize: 24, fontWeight: 700 }}>
                  {consumption.kwh.toFixed(3)}
                  <Typography component="span" sx={{ color: `${c.text}B3`, fontSize: 14 }}> kWh</Typography>
                </Typography>
                <Typography sx={{ color: `${c.text}B3`, fontSize: 14 }}>
                  {Math.round(consumption.wh)} Wh
                </Typography>
                <Typography sx={{ color: '#4CAF50', fontSize: 16, fontWeight: 700 }}>
                  {`≈ ₹${consumption.bill.toFixed(2)}`}
                </Typography>
              </Box>
            ) : (
              <Typography sx={{ color: `${c.text}B3` }}>--</Typography>
            )}
          </Box>

          {/* "Pick a Date" — a one-day shortcut. The native picker sits
              invisibly over the button so Android opens its own. */}
          <Box sx={{ position: 'relative', display: 'inline-flex', mt: 2.5 }}>
            <Button
              startIcon={<CalendarTodayIcon sx={{ color: c.text }} />}
              sx={{
                bgcolor: c.button,
                color: c.text,
                py: 2,
                px: 3,
                borderRadius: '12px',
                textTransform: 'none',
                '&:hover': { bgcolor: c.button },
              }}
            >
              Pick a Date
            </Button>
            <Box
              component="input"
              type="date"
              onChange={onPickDate}
              sx={{
                position: 'absolute',
                inset: 0,
                opacity: 0,
                border: 0,
                padding: 0,
                width: '100%',
                height: '100%',
              }}
            />
          </Box>
        </Box>
      </Box>
    </Box>
  );
}
